// ============================================================================
// C:\VUE3\platform\src\main.js (SEO 개선 버전)
// ============================================================================

// ------------------------------------
// 1) 폰트
// -----------------------------------
// // 성능 문제로 임시 구글 cdn 으로 전환
// import '@fontsource/noto-sans-kr'; 

// ------------------------------------
// 2) 프레임워크/라이브러리 CSS
// ------------------------------------
import '@/assets/resources/css/custom-bootstrap.scss';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'vue-color-kit/dist/vue-color-kit.css';
import 'vue-toast-notification/dist/theme-sugar.css';
import '@/assets/resources/css/toast-override.css';
import "@/assets/resources/css/swiper-mpnite.css";
import '@fortawesome/fontawesome-svg-core/styles.css';
import 'leaflet/dist/leaflet.css';

// ------------------------------------
// 3) 프로젝트 CSS
// ------------------------------------
import '@/assets/resources/css/basic.css';
import '@/assets/resources/css/common.css';
import '@/assets/resources/css/login.css';
import '@/assets/resources/css/map.scss';

// ------------------------------------
// 4) Bootstrap JS 번들
// ------------------------------------
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// ------------------------------------
// 5) Vue & Plugins
// ------------------------------------
import { createApp, nextTick } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createHead } from '@unhead/vue/client';

import App from '@/App.vue';
import router from '@/router';
import axios from 'axios';
import VueCookies from 'vue-cookies';
import i18n from '@/i18n';
import { quillEditor } from 'vue3-quill';
import VueColorKit from 'vue-color-kit';

import VueToast, { useToast } from 'vue-toast-notification';

import loggerPlugin, { logger } from '@/plugins/logger';
import globalErrorHandler from '@/plugins/globalErrorHandler';
import { logInfo, logWarn, logError } from '@/utils/logHelper';

import { useMaterialScoreStore } from '@/store/materialScoreStore';
import { useColorPaletteStore } from '@/store/colorPaletteStore';

import {
  config as faConfig,
  library,
} from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faUser, faXmark } from '@fortawesome/free-solid-svg-icons';

import { checkAndClearCache } from '@/utils/cacheBuster';
import { createGtmPlugin } from '@/plugins/gtm';

faConfig.autoAddCss = false;
library.add(faUser, faXmark);

// ------------------------------------
// 6) 봇/프리렌더 감지 유틸
// ------------------------------------
function isPrerendererOrBot() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return true;
  }
  
  const ua = navigator.userAgent || '';
  
  if (window.__PRERENDER_INJECTED || ua.includes('HeadlessChrome')) {
    return true;
  }
  
  const botPatterns = [
    'Googlebot', 'Bingbot', 'Yeti', 'Naverbot', 'Daumoa',
    'Slurp', 'DuckDuckBot', 'Baiduspider', 'YandexBot',
    'facebookexternalhit', 'Twitterbot', 'LinkedInBot',
    'Applebot', 'PetalBot', 'SemrushBot', 'AhrefsBot',
  ];
  
  return botPatterns.some(bot => ua.includes(bot));
}

// ------------------------------------
// 7) 앱 생성
// ------------------------------------
const app = createApp(App);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// ✅ 수정: createHead는 옵션 없이 생성
// titleTemplate은 App.vue의 useHead()에서 직접 처리
const head = createHead();

// ------------------------------------
// 8) 플러그인 등록
// ------------------------------------
app.use(head);
app.use(pinia);
app.use(router);
app.use(VueCookies);
app.use(i18n);
app.use(quillEditor);
app.use(VueColorKit);

app.use(VueToast, { position: 'top-right' });

const gtmPlugin = createGtmPlugin(router);
if (gtmPlugin) {
  app.use(gtmPlugin);
  logInfo('✅ GTM Plugin registered:', process.env.VUE_APP_GTM_ID);
}

app.use(loggerPlugin);
app.use(globalErrorHandler);

app.component('font-awesome-icon', FontAwesomeIcon);

// ------------------------------------
// 9) 전역 프로퍼티
// ------------------------------------
app.config.globalProperties.$axios = axios;
app.config.globalProperties.$cookie = VueCookies;
app.config.globalProperties.$toast = useToast();
app.config.globalProperties.$cookies?.config('7d');

// ------------------------------------
// 10) 환경 로그 (런타임 진단 배너)
//    - 새로고침 후 콘솔에서 어느 환경/백엔드에 연결됐는지 즉시 확인용
//    - 운영 IP/도메인이면 "PROD" 라벨, 그 외면 "DEV" 라벨로 표시
// ------------------------------------
(() => {
  const mode = process.env.VUE_APP_MODE || process.env.NODE_ENV || 'unknown';
  const api = process.env.VUE_APP_API_URL || '';
  const image = process.env.VUE_APP_IMAGE_BASE_PATH || '';
  const fastapi = process.env.VUE_APP_BASE_FASTAPI_URL || '';
  const site = process.env.VUE_APP_SITE_URL || '';

  // mpnite.com 운영 도메인을 포함하면 운영, 그 외에는 dev 로 추정
  const looksProd = (url) => /mpnite\.com/i.test(url);
  const labelOf = (url) => (url ? (looksProd(url) ? 'PROD' : 'DEV') : '-');

  const banner = [
    '%c🔧 RUNTIME ENVIRONMENT',
    `Mode    : ${mode}`,
    `API     : ${api}  [${labelOf(api)}]`,
    `Image   : ${image}  [${labelOf(image)}]`,
    `FastAPI : ${fastapi}  [${labelOf(fastapi)}]`,
    `Site    : ${site}`,
  ].join('\n');

  // eslint-disable-next-line no-console
  console.log(banner, 'font-weight:bold;color:#0d6efd;');

  // 기존 logInfo 도 유지 (logHelper 의 레벨 필터링 활용)
  logInfo('VUE_APP_COMMON_MSG:', process.env.VUE_APP_COMMON_MSG);
  logInfo('VUE_APP_API_URL:', api);
  logInfo('VUE_APP_MODE:', mode);
})();

// ------------------------------------
// 11) 앱 초기화 및 마운트
// ------------------------------------
async function bootstrap() {
  const willReload = await checkAndClearCache();
  
  if (willReload) {
    logInfo('🔄 새로고침 대기 중...');
    return;
  }

  const isBotOrPrerender = isPrerendererOrBot();
  
  if (isBotOrPrerender) {
    logInfo('🤖 Bot/Prerenderer detected - skipping API calls');
  }

  const materialScoreStore = useMaterialScoreStore(pinia);
  const colorPaletteStore = useColorPaletteStore(pinia);

  if (!isBotOrPrerender) {
    try {
      await Promise.allSettled([
        materialScoreStore.fetchMaterialScores(),
        colorPaletteStore.fetchColorData(),
      ]);
      logInfo('✅ Bootstrap data loaded successfully');
    } catch (e) {
      logError('[Bootstrap] Initialization failed', e);
    }
  }

  router.isReady().then(async () => {
    app.mount('#app');
    
    await nextTick();
    await nextTick();
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const title = document.querySelector('title');
        const canonical = document.querySelector('link[rel="canonical"]');
        
        if (title && canonical) {
          document.dispatchEvent(new Event('render-event'));
          logInfo('✅ Prerender event dispatched with meta ready');
        } else {
          logWarn('⚠️ Meta tags not ready, delaying prerender');
          setTimeout(() => {
            document.dispatchEvent(new Event('render-event'));
          }, 100);
        }
      });
    });
  });
}

bootstrap();